<template>
  <span :class="$style['more-info']">
    <slot>
      <mds-button
        v-if="showInfoIcons"
        :id="`infoTooltip${uniqueId}`"
        :class="$style['more-info__icon']"
        variation="icon-only"
        size="small"
        icon="info-circle"
        type="button"
        :text="labels.modalTitle"
        @click="showInfoTooltip = !showInfoTooltip"
      >
      </mds-button>
    </slot>
    <mds-popover
      v-model="showInfoTooltip"
      :triggered-by="`infoTooltip${uniqueId}`"
      :position="tooltipPosition"
      :width="popoverWidth"
    >
      <slot name="abbr" :showLearnMore="showLearnMore" :content="abbrContentHTML">
        <!-- eslint-disable-next-line vue/no-v-html -->
        <span v-html="abbrContentHTML"></span>
      </slot>
      <template v-if="showLearnMore">
        <div :class="$style['more-info-link__wrapper']">
          <a href="#" :class="$style['more-info-link']" @click.prevent="openLearnMoreModal">
            {{ labels.learnMore }}
          </a>
        </div>
      </template>
    </mds-popover>
    <mds-modal
      ref="learnMoreModal"
      v-model="showLearnMoreModal"
      :aria-labelledby="`infoModalTitle${uniqueId}`"
      :class="$style['more-info__modal']"
      width="600px"
      action-required
    >
      <mds-section border-position="bottom" bold>
        <template v-slot:mds-section-title>
          <span :id="`infoModalTitle${uniqueId}`">{{ labels.modalTitle }}</span>
        </template>
        <template v-slot:mds-section-actions>
          <mds-button-container right-aligned>
            <mds-button variation="primary" @click="showLearnMoreModal = !showLearnMoreModal">
              {{ labels.close }}
            </mds-button>
          </mds-button-container>
        </template>
        <template v-if="detailedContentHTML !== ''">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <p :class="$style['more-info__abbr']" v-html="abbrContentHTML"></p>
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="detailedContentHTML"></div>
        </template>
        <slot
          v-else
          name="more"
          :showLearnMore="showLearnMore"
          :abbrContent="abbrContentHTML"
          :detailedContent="detailedContentHTML"
        ></slot>
      </mds-section>
    </mds-modal>
  </span>
</template>

<script>
import { MdsButtonContainer, MdsButton } from '@mds/button';
import MdsModal from '@mds/modal';
import MdsSection from '@mds/section';
import MdsPopover from './ScrollablePopover';
import SanitizeMixin from '../mixins/sanitizeMixin';

let uniqueId = 1;
export default {
  name: 'more-info',
  components: {
    MdsButtonContainer,
    MdsButton,
    MdsPopover,
    MdsModal,
    MdsSection,
  },
  mixins: [SanitizeMixin],
  props: {
    showInfoIcons: {
      type: Boolean,
      default: true,
    },
    popoverWidth: {
      type: String,
      default: '300px',
    },
    tooltipPosition: {
      type: Array,
      default: () => [
        'bottom-center',
        'bottom-right',
        'right-center',
        'top-right',
        'top-center',
        'top-left',
        'left-center',
        'bottom-left',
      ],
    },
    showLearnMore: {
      type: Boolean,
      default: true,
    },
    labels: {
      type: Object,
      default: () => ({
        learnMore: 'Learn More',
        close: 'Close',
        modalTitle: 'More Information',
      }),
    },
    abbrContent: {
      type: String,
      default: '',
    },
    detailedContent: {
      type: String,
      default: '',
    },
    appendToBody: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      showInfoTooltip: false,
      showLearnMoreModal: false,
    };
  },
  computed: {
    abbrContentHTML() {
      return this.sanitizedHtml(this.abbrContent);
    },
    detailedContentHTML() {
      return this.sanitizedHtml(this.detailedContent);
    },
  },
  created() {
    this.uniqueId = uniqueId;
    uniqueId += 1;
  },
  mounted() {
    this.appendModalToBody();
  },
  updated() {
    this.appendModalToBody();
  },
  destroyed() {
    this.removeModalFromBody();
  },
  methods: {
    appendModalToBody() {
      if (this.appendToBody && this.$refs.learnMoreModal) {
        document.body.appendChild(this.$refs.learnMoreModal.$el);
      }
    },
    removeModalFromBody() {
      if (this.appendToBody && this.$refs.learnMoreModal) {
        document.body.removeChild(this.$refs.learnMoreModal.$el);
      }
    },
    openLearnMoreModal() {
      this.showInfoTooltip = false;
      this.showLearnMoreModal = !this.showLearnMoreModal;
    },
  },
};
</script>

<style lang="scss" module>
@import '~@mds/constants';
@import '~@mds/typography';
@import '~@mds/link';

.more-info {
  display: inline-flex;
  align-items: center;
  margin: 0 $mds-space-half-x;
}
.more-info__abbr {
  margin: 0 0 $mds-space-2-x;
}

.more-info__modal .mds-modal__content {
  max-height: 80vh;
  > .mds-section {
    height: 100%;
    > .mds-section__content {
      height: calc(100% - 40px);
      overflow: auto;
    }
  }
}

.more-info-link__wrapper {
  display: flex;
  .more-info-link {
    margin-top: $mds-space-three-quarter-x;
    margin-left: auto;
    @include mds-link();
    color: $mds-text-color-interactive-primary;
  }
}
</style>
